import { cellIndex } from '@p4p2r0/minesweeper-core';
import type { GameState } from '@p4p2r0/minesweeper-core';
import React, { useRef, useState } from 'react';

import { FlagIcon, MineIcon, WrongFlagIcon } from './Icons';

interface BoardProps {
  state: GameState;
  disabled: boolean;
  onReveal: (x: number, y: number) => void;
  onFlag: (x: number, y: number) => void;
  onChord: (x: number, y: number) => void;
  /** Drives the face button's nervous expression while a cell is held. */
  onPressChange: (pressing: boolean) => void;
  /** A cell to draw attention to — the tutorial's way of pointing without a screenshot. */
  hintIndex?: number | null;
  /**
   * Swaps which gesture flags and which reveals. Off: tap reveals, long-press
   * or right-click flags (the classic scheme). On: tap flags, long-press or
   * right-click reveals — built for trackpads, where the classic scheme's
   * right-click/long-press half is awkward.
   */
  flagMode?: boolean;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
/** Pixels a single-finger touch has to travel before it counts as a pan
    rather than a tap — otherwise every zoomed-in tap-to-reveal would also
    register as a (zero-distance) drag. */
const PAN_THRESHOLD_PX = 8;

const LONG_PRESS_MS = 350;
// A flag toggle within this long of the previous one, on the same cell, is
// treated as a duplicate rather than a deliberate double-tap — guards against
// an intermittent double-fire (seen on some trackpads' two-finger-tap-to-
// right-click gesture) sending two flag toggles for what was physically one
// click, which reads as "the flag I just placed vanished."
const FLAG_DEBOUNCE_MS = 300;

export const Board = React.memo(function Board({
  state,
  disabled,
  onReveal,
  onFlag,
  onChord,
  onPressChange,
  hintIndex = null,
  flagMode = false,
}: BoardProps): React.ReactElement {
  const { width, height } = state.config;
  // Mines aren't placed until the first reveal (it has to land somewhere
  // safe), so nothing can be flagged before then — the server silently drops
  // a flag sent before that point. The very first tap on a fresh board must
  // always reveal, flag mode or not, or the game could never start.
  const boardStarted = state.board !== null;
  // On a loss the mine you hit is highlighted differently from the rest; on a
  // win there is no "the" mine, but showing them all — muted, inert — makes it
  // visually obvious the board is done rather than leaving cells that still
  // look exactly like ordinary, clickable, covered squares.
  const revealMines = state.status === 'lost' || state.status === 'won';

  // A long press flags. The ref stops the click that follows the press from
  // also revealing the cell we just flagged.
  const pressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);

  // Where the most recent click landed, so a cascade can ripple outwards from
  // it rather than all popping at once.
  const origin = useRef({ x: 0, y: 0 });

  const lastFlagAt = useRef<Map<number, number>>(new Map());
  const toggleFlag = (x: number, y: number): void => {
    const index = cellIndex(width, x, y);
    const now = Date.now();
    const last = lastFlagAt.current.get(index) ?? 0;
    if (now - last < FLAG_DEBOUNCE_MS) return;
    lastFlagAt.current.set(index, now);
    onFlag(x, y);
  };

  /**
   * Cells that opened on this move, mapped to their distance from the click —
   * which becomes their animation delay, producing the ripple.
   *
   * Guarded by the identity of `state.revealed` rather than recomputed each
   * render: the engine hands back a fresh array only when something actually
   * opened, and the guard keeps StrictMode's double-render (and the ticking
   * clock) from diffing an array against a copy of itself and finding nothing.
   */
  const cache = useRef<{
    seen: Uint8Array | null;
    previous: Uint8Array | null;
    delays: Map<number, number>;
  }>({ seen: null, previous: null, delays: new Map() });

  if (cache.current.seen !== state.revealed) {
    const previous = cache.current.previous;
    const current = state.revealed;
    const delays = new Map<number, number>();

    if (previous && previous.length === current.length) {
      for (let i = 0; i < current.length; i++) {
        if (current[i] === 1 && previous[i] === 0) {
          const x = i % width;
          const y = (i - x) / width;
          delays.set(i, Math.max(Math.abs(x - origin.current.x), Math.abs(y - origin.current.y)));
        }
      }
    }

    cache.current = { seen: current, previous: new Uint8Array(current), delays };
  }
  const opening = cache.current.delays;

  // --- pinch-to-zoom + pan ---------------------------------------------
  //
  // Native pinch-zoom on mobile scales the whole *page*, which is exactly
  // the wrong thing here: the HUD and toasts zoom along with the board, it's
  // easy to end up scroll-shifted afterwards, and — on top of that — some
  // mobile browsers render the board's many shadowed, bordered cells solid
  // black mid-pinch (a known compositor glitch with large grids of
  // box-shadow'd elements during viewport-level zoom). Handling zoom/pan
  // ourselves with a CSS `transform` on `.board` sidesteps both: it stays
  // contained to the board, and it's an ordinary transform the compositor
  // handles cleanly.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [gesturing, setGesturing] = useState(false);
  const viewRef = useRef(view);
  viewRef.current = view;

  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);
  const dragged = useRef(false);
  // A tap's `click` can land just after a pan/pinch ends; this blocks the
  // reveal/flag it would otherwise trigger, regardless of which cell it
  // lands on.
  const suppressClickUntil = useRef(0);
  const lastTap = useRef(0);

  const clampView = (scale: number, x: number, y: number): { scale: number; x: number; y: number } => {
    const clampedScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
    const wrap = wrapRef.current;
    const board = boardRef.current;
    if (!wrap || !board) return { scale: clampedScale, x, y };
    const maxX = Math.max(0, (board.offsetWidth * clampedScale - wrap.clientWidth) / 2);
    const maxY = Math.max(0, (board.offsetHeight * clampedScale - wrap.clientHeight) / 2);
    return {
      scale: clampedScale,
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const resetView = (): void => setView({ scale: 1, x: 0, y: 0 });

  const distance = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
    Math.hypot(a.x - b.x, a.y - b.y);

  /** Whether there's anything to pan to at all, at the given scale — a board
      that fits entirely within its wrapper has nowhere to go, and treating a
      sloppy tap as a drag in that case would suppress ordinary reveals for
      no reason. */
  const hasOverflow = (scale: number): boolean => {
    const wrap = wrapRef.current;
    const board = boardRef.current;
    if (!wrap || !board) return false;
    return board.offsetWidth * scale > wrap.clientWidth + 0.5 || board.offsetHeight * scale > wrap.clientHeight + 0.5;
  };

  // Failing to capture shouldn't abort the gesture — it's an optimisation
  // (keeps tracking a fast-moving finger past cell boundaries), not a
  // precondition; without the guard, a capture failure here used to silently
  // skip the rest of pinch setup and the gesture just wouldn't zoom at all.
  const tryCapture = (pointerId: number): void => {
    try {
      wrapRef.current?.setPointerCapture(pointerId);
    } catch {
      // Nothing to do — tracking still works via ordinary bubbling.
    }
  };

  const onWrapPointerDown = (event: React.PointerEvent): void => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      cancelPress();
      tryCapture(event.pointerId);
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: Math.max(1, distance(a, b)), scale: viewRef.current.scale };
      panStart.current = null;
      setGesturing(true);
    } else if (pointers.current.size === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        resetView();
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
      dragged.current = false;
      // Only armed when there's actual overflow to pan through — otherwise
      // left null, so `onWrapPointerMove` leaves an ordinary tap alone.
      panStart.current = hasOverflow(viewRef.current.scale)
        ? { x: event.clientX, y: event.clientY, originX: viewRef.current.x, originY: viewRef.current.y }
        : null;
    }
  };

  const onWrapPointerMove = (event: React.PointerEvent): void => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const scale = pinchStart.current.scale * (distance(a, b) / pinchStart.current.dist);
      setView((current) => clampView(scale, current.x, current.y));
      return;
    }

    if (pointers.current.size === 1 && panStart.current) {
      const dx = event.clientX - panStart.current.x;
      const dy = event.clientY - panStart.current.y;
      if (!dragged.current && Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return;
      if (!dragged.current) {
        dragged.current = true;
        cancelPress();
        tryCapture(event.pointerId);
        setGesturing(true);
      }
      setView((current) => clampView(current.scale, panStart.current!.originX + dx, panStart.current!.originY + dy));
    }
  };

  const endPointer = (event: React.PointerEvent): void => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 1) {
      const [remaining] = [...pointers.current.values()];
      panStart.current = { x: remaining.x, y: remaining.y, originX: viewRef.current.x, originY: viewRef.current.y };
    }
    if (pointers.current.size === 0) {
      panStart.current = null;
      setGesturing(false);
      if (dragged.current) {
        suppressClickUntil.current = Date.now() + 200;
        dragged.current = false;
      }
    }
  };

  const cancelPress = (): void => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    onPressChange(false);
  };

  const startPress = (x: number, y: number, event: React.PointerEvent): void => {
    if (disabled) return;
    // Long-press-to-flag is a touch/pen affordance; a mouse already has its
    // own dedicated flag gesture (right-click, below). Arming this timer for
    // a right-button press too left a *second*, unrelated flag toggle
    // pending — if the mouse button was released even slightly later than
    // usual, that phantom timer fired ~350ms after the click and silently
    // flipped the flag straight back off. That was the "flag disappears/
    // moves half a second after placement" bug.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    longPressed.current = false;
    onPressChange(true);
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      origin.current = { x, y };
      // Long-press is the *other* gesture from a tap, whichever mode that
      // currently is — reveal in flag mode, flag otherwise. Before the board
      // exists, flagging is a no-op no matter the gesture, so this always
      // reveals instead, same as the tap would.
      //
      // No unconditional vibrate here: a long-press on a cell with nothing
      // left to do — already revealed, nothing to flag — used to buzz
      // anyway, since this fired regardless of whether toggleFlag/onReveal
      // actually changed anything. The flag path's real feedback already
      // happens in the caller's onFlag, gated on `session.flag` actually
      // returning true; duplicating it here unconditionally is what made
      // "not clicking on clickable places" (an inert, already-open cell)
      // still buzz on a long-press.
      if (!boardStarted || flagMode) onReveal(x, y);
      else toggleFlag(x, y);
    }, LONG_PRESS_MS);
  };

  const activate = (x: number, y: number): void => {
    if (disabled) return;
    if (Date.now() < suppressClickUntil.current) return;
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    origin.current = { x, y };
    const index = cellIndex(width, x, y);
    // Chording an already-open cell is its own action, unaffected by mode.
    // On a covered cell, a plain tap flags or reveals depending on mode —
    // except before the board exists, when only a reveal does anything.
    if (state.revealed[index] === 1) onChord(x, y);
    else if (flagMode && boardStarted) toggleFlag(x, y);
    else onReveal(x, y);
  };

  const cells: React.ReactElement[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = cellIndex(width, x, y);
      const isMine = state.board !== null && state.board.mine[index] === 1;
      const isRevealed = state.revealed[index] === 1;
      const isFlagged = state.flagged[index] === 1;
      const adjacent = state.board ? state.board.adjacent[index] : 0;

      const classes = ['cell'];
      let label = '';
      let content: React.ReactNode = null;
      // What a screen reader hears. `aria-label` replaces the cell's visible
      // contents outright, so leaving it at "row 3 column 4" meant the grid
      // read as an undifferentiated field of identical buttons — no numbers,
      // no flags, no way to play it at all.
      let described = 'covered';

      if (isFlagged) {
        classes.push('flagged');
        if (revealMines && !isMine) {
          classes.push('wrong');
          label = 'wrong';
          described = 'wrongly flagged';
          content = <WrongFlagIcon />;
        } else {
          label = 'flag';
          described = 'flagged';
          content = <FlagIcon />;
        }
      } else if (isRevealed) {
        classes.push('open');
        if (opening.has(index)) classes.push('opening');
        if (isMine) {
          classes.push('mine');
          if (index === state.explodedAt) classes.push('exploded');
          label = 'mine';
          described = index === state.explodedAt ? 'the mine you hit' : 'mine';
          content = <MineIcon />;
        } else if (adjacent > 0) {
          classes.push(`n${adjacent}`);
          label = String(adjacent);
          described = `${adjacent} ${adjacent === 1 ? 'mine' : 'mines'} nearby`;
          content = adjacent;
        } else {
          described = 'empty';
        }
      } else if (revealMines && isMine) {
        classes.push('open', 'mine');
        label = 'mine';
        described = 'mine';
        content = <MineIcon />;
      }

      if (index === hintIndex) classes.push('hint');

      cells.push(
        <button
          key={index}
          type="button"
          className={classes.join(' ')}
          disabled={disabled}
          style={
            {
              '--d': opening.get(index) ?? 0,
              '--s': x + y,
            } as React.CSSProperties
          }
          aria-label={`row ${y + 1}, column ${x + 1}, ${described}`}
          onPointerDown={(event) => startPress(x, y, event)}
          onPointerUp={cancelPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
          onClick={() => activate(x, y)}
          onContextMenu={(event) => {
            event.preventDefault();
            if (disabled) return;
            // On a touch device, a long press fires *both* our own timer
            // above and, shortly after, this native contextmenu event — the
            // same physical press otherwise triggering onFlag twice in a row
            // (once from each), which looked like a flag being placed and
            // then silently un-placing itself under a second later. If our
            // timer already handled this press, this is that second firing.
            // Cancelling the timer here too (belt and suspenders alongside
            // the pointerType/button guard in startPress) means a right
            // click can never leave one armed behind it, on any browser's
            // event ordering.
            cancelPress();
            if (longPressed.current) {
              longPressed.current = false;
              return;
            }
            origin.current = { x, y };
            // Right-click is the other gesture from a tap, same as long-press.
            const index = cellIndex(width, x, y);
            if (state.revealed[index] === 1) onChord(x, y);
            else if (!boardStarted || flagMode) onReveal(x, y);
            else toggleFlag(x, y);
          }}
          onAuxClick={(event) => {
            // Middle click is the traditional chord.
            if (event.button !== 1) return;
            event.preventDefault();
            if (disabled) return;
            cancelPress();
            origin.current = { x, y };
            onChord(x, y);
          }}
        >
          {content !== null ? (
            <span className="glyph" key={label}>
              {content}
            </span>
          ) : null}
        </button>,
      );
    }
  }

  const boardClasses = ['board'];
  if (state.status === 'lost') boardClasses.push('shake');
  if (!gesturing) boardClasses.push('view-settled');
  // Every cell carries its own inset box-shadow bevel; hundreds of them
  // composited under a scale transform is what the mobile black/white-out
  // (see the CSS comment above `.board-wrap`) actually turns out to be — not
  // gesture handling, but raster cost. Dropping the bevel to a flat border
  // for as long as the board is zoomed removes that cost exactly when the
  // transform is active, without touching the un-zoomed look at all.
  if (view.scale > 1.02) boardClasses.push('zoomed');

  return (
    <div
      className="board-wrap"
      ref={wrapRef}
      onPointerDown={onWrapPointerDown}
      onPointerMove={onWrapPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <div
        ref={boardRef}
        className={boardClasses.join(' ')}
        style={
          {
            '--cols': String(width),
            '--rows': String(height),
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          } as React.CSSProperties
        }
        role="grid"
        aria-label="minefield"
      >
        {cells}
      </div>
      {view.scale > 1.02 ? (
        <button type="button" className="board-zoom-reset" onClick={resetView}>
          {Math.round(view.scale * 100)}%
        </button>
      ) : null}
    </div>
  );
});
